import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { BACKEND_URL } from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Trophy, ArrowRight, ChartBar, Lock, Clock, Users, MagnifyingGlass, ShieldCheck, Calendar, Timer, SquaresFour, ListBullets, Download, Printer, Flag, UserCircleGear, Camera, MapPin } from "@phosphor-icons/react";
import { motion } from "framer-motion";

function fmtDt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
}

function countdown(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return null;
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
}

/**
 * Prints the Results page with a dynamic @page footer that includes the
 * election title, live tally, timestamp and CSS `counter(page) " of " counter(pages)`
 * page numbers. Uses an injected <style> block so the strings can be filled with
 * the current election's data (CSS `content` can't read React state directly).
 */
function printCertifiedSheet(data) {
    const escCss = (s) => (s || "").toString().replace(/"/g, '\\"').replace(/\n/g, " ");
    const title = escCss(data.election.title);
    const authority = escCss(data.election.authority || "—");
    const totalVotes = data.total_votes || 0;
    const eligible = data.eligible_voters ?? "—";
    const pct = data.eligible_voters ? Math.round((totalVotes / data.eligible_voters) * 100) : 0;
    const printedAt = escCss(new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }));

    const styleId = "certified-print-page-style";
    document.getElementById(styleId)?.remove();
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
        @page {
            size: Letter;
            margin: 0.4in 0.4in 0.75in 0.4in;
            @bottom-left {
                content: "${title} · ${authority}";
                font: 600 7.5pt ui-monospace, SFMono-Regular, Menlo, monospace;
                color: #475569;
                padding-top: 4pt;
                border-top: 1px solid #cbd5e1;
                vertical-align: top;
            }
            @bottom-center {
                content: "Certified tally · ${totalVotes} of ${eligible} voters (${pct}%) · Printed ${printedAt}";
                font: 7.5pt ui-monospace, SFMono-Regular, Menlo, monospace;
                color: #475569;
                padding-top: 4pt;
                border-top: 1px solid #cbd5e1;
                vertical-align: top;
            }
            @bottom-right {
                content: "Page " counter(page) " of " counter(pages);
                font: 600 7.5pt ui-monospace, SFMono-Regular, Menlo, monospace;
                color: #0f172a;
                padding-top: 4pt;
                border-top: 1px solid #cbd5e1;
                vertical-align: top;
            }
        }
    `;
    document.head.appendChild(style);
    // Small delay so the injected @page rules are applied before the print dialog opens.
    setTimeout(() => {
        window.print();
        // Leave the injected style — some browsers re-parse @page on subsequent prints.
    }, 50);
}


export default function Results() {
    const { id } = useParams();
    if (id) return <ElectionResults id={id} />;
    return <ResultsList />;
}

function ResultsList() {
    const [els, setEls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setTick] = useState(0);
    const [view, setView] = useState(() => {
        try { return localStorage.getItem("results_view") || "cards"; } catch { return "cards"; }
    });
    const changeView = (v) => {
        setView(v);
        try { localStorage.setItem("results_view", v); } catch { /* ignore */ }
    };

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/elections"); setEls(data); }
            catch {}
            setLoading(false);
        })();
    }, []);
    // Re-render every second so countdowns stay live.
    useEffect(() => {
        const t = setInterval(() => setTick((x) => x + 1), 1000);
        return () => clearInterval(t);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                    <div className="min-w-0">
                        <div className="overline text-civic-600 text-[10px]">Live tallies</div>
                        <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mt-0.5 text-slate-900 break-words leading-tight">Election Results</h1>
                        <p className="text-xs text-slate-600 mt-1">Anonymous totals, computed directly from the chain. Tallies are sealed until polls close.</p>
                    </div>
                    {/* View toggle — Cards / Table */}
                    <div className="inline-flex rounded-md border border-slate-300 bg-white p-0.5" data-testid="results-view-toggle">
                        <button
                            type="button"
                            onClick={() => changeView("cards")}
                            className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-semibold transition-colors ${view === "cards" ? "bg-civic-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                            data-testid="results-view-cards"
                            aria-pressed={view === "cards"}
                        >
                            <SquaresFour size={13} weight="bold" /> Cards
                        </button>
                        <button
                            type="button"
                            onClick={() => changeView("table")}
                            className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded text-xs font-semibold transition-colors ${view === "table" ? "bg-civic-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                            data-testid="results-view-table"
                            aria-pressed={view === "table"}
                        >
                            <ListBullets size={13} weight="bold" /> Table
                        </button>
                    </div>
                </div>

                {loading ? <div className="hash-mono text-slate-500 text-sm">loading…</div> :
                    els.length === 0 ? (
                        <Card className="p-10 text-center bg-white border-slate-200" data-testid="no-public-elections">
                            <ChartBar size={32} weight="duotone" className="text-civic-600 mx-auto mb-3" />
                            <div className="font-heading font-bold text-lg text-slate-900">No published elections</div>
                            <p className="text-sm text-slate-500 mt-2">Results will appear once an election goes live.</p>
                        </Card>
                    ) : view === "table" ? (
                        <ResultsTableView els={els} />
                    ) : (
                        <div className="grid md:grid-cols-2 gap-3" data-testid="public-elections-list">
                            {els.map((e) => {
                                const remaining = e.is_over ? null : countdown(e.ends_at);
                                return (
                                    <Link key={e.id} to={`/results/${e.id}`} data-testid={`public-election-${e.id}`}>
                                        <Card className="p-3 bg-white border-slate-200 hover:border-civic-300 hover:-translate-y-0.5 transition-all">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <Badge className={`text-[10px] px-1.5 py-0 h-5 ${e.results_hidden ? "bg-amber-100 text-amber-900" : e.is_over ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"} border-0`}>
                                                    {e.results_hidden ? "POLLS CLOSED · RESULTS HIDDEN" : (e.is_over ? "POLLS CLOSED · RESULTS LIVE" : "POLLS OPEN · RESULTS SEALED")}
                                                </Badge>
                                                {!e.is_over && remaining && (
                                                    <Badge variant="outline" className="border-civic-200 bg-civic-50 text-civic-700 hash-mono text-[10px] h-5 px-1.5 py-0">
                                                        <Clock size={10} weight="bold" className="mr-1" /> {remaining}
                                                    </Badge>
                                                )}
                                                {e.secure_election && (
                                                    <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700 text-[10px] h-5 px-1.5 py-0 font-bold uppercase tracking-wider" data-testid={`public-secure-${e.id}`}>
                                                        <Camera size={10} weight="fill" className="mr-1" /> Secure
                                                    </Badge>
                                                )}
                                            </div>
                                            <h3 className="font-heading font-bold text-base text-slate-900 mt-2 leading-tight">{e.title}</h3>
                                            {e.description && <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{e.description}</p>}
                                            <div className="hash-mono text-[11px] text-slate-500 mt-1">
                                                {fmtDt(e.starts_at)} → {fmtDt(e.ends_at)}
                                            </div>
                                            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between">
                                                <div>
                                                    <div className="overline text-slate-500 text-[9px]">Votes on chain</div>
                                                    {e.results_hidden ? (
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <Lock size={12} weight="fill" className="text-amber-500" />
                                                            <span className="hash-mono text-xs font-semibold text-amber-700">HIDDEN</span>
                                                        </div>
                                                    ) : e.is_over ? (
                                                        <div className="hash-mono text-lg font-semibold text-civic-700 leading-tight mt-0.5">{e.votes_count}</div>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <Lock size={12} weight="fill" className="text-slate-400" />
                                                            <span className="hash-mono text-xs font-semibold text-slate-500">SEALED</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <ArrowRight size={16} className="text-civic-600" />
                                            </div>
                                        </Card>
                                    </Link>
                                );
                            })}
                        </div>
                    )
                }
            </div>
        </div>
    );
}

function ResultsTableView({ els }) {
    return (
        <Card className="overflow-hidden bg-white border-slate-200" data-testid="results-table-view">
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                            <th className="px-3 py-2 font-bold">Status</th>
                            <th className="px-3 py-2 font-bold">Election</th>
                            <th className="px-3 py-2 font-bold hidden sm:table-cell">Authority</th>
                            <th className="px-3 py-2 font-bold hidden md:table-cell">Window</th>
                            <th className="px-3 py-2 font-bold text-right">Votes</th>
                            <th className="px-3 py-2 font-bold text-right">Time / Open</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {els.map((e, i) => {
                            const remaining = e.is_over ? null : countdown(e.ends_at);
                            return (
                                <tr
                                    key={e.id}
                                    className={`border-b border-slate-100 hover:bg-civic-50/40 transition-colors cursor-pointer ${i === els.length - 1 ? "border-b-0" : ""}`}
                                    onClick={() => { window.location.href = `/results/${e.id}`; }}
                                    data-testid={`results-table-row-${e.id}`}
                                >
                                    <td className="px-3 py-2.5 whitespace-nowrap">
                                        <Badge className={`text-[10px] px-1.5 py-0 h-5 ${e.is_over ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-900 border-amber-200"} border`}>
                                            {e.is_over ? "Closed" : "Open"}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <div className="font-semibold text-slate-900 leading-tight">{e.title}</div>
                                            {e.secure_election && (
                                                <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5" title="Secure election — voter photo captured at submission" data-testid={`results-row-secure-${e.id}`}>
                                                    <Camera size={9} weight="fill" /> Secure
                                                </span>
                                            )}
                                        </div>
                                        {e.description && <div className="text-[11px] text-slate-500 truncate max-w-md">{e.description}</div>}
                                    </td>
                                    <td className="px-3 py-2.5 hidden sm:table-cell text-slate-700 text-xs">{e.authority || "—"}</td>
                                    <td className="px-3 py-2.5 hidden md:table-cell hash-mono text-[11px] text-slate-600 whitespace-nowrap">
                                        {fmtDt(e.starts_at)} → {fmtDt(e.ends_at)}
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        {e.is_over ? (
                                            <span className="hash-mono font-semibold text-civic-700">{e.votes_count}</span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 hash-mono text-xs text-slate-500">
                                                <Lock size={11} weight="fill" /> sealed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                                        {e.is_over ? (
                                            <span className="text-[11px] text-slate-500">—</span>
                                        ) : remaining ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-civic-700 hash-mono">
                                                <Clock size={10} weight="bold" /> {remaining}
                                            </span>
                                        ) : (
                                            <span className="text-[11px] text-amber-700">opens soon</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <ArrowRight size={14} className="text-civic-600 inline-block" />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function ElectionResults({ id }) {
    const [data, setData] = useState(null);
    const [, setTick] = useState(0);

    const load = async () => {
        try { const { data } = await api.get(`/elections/${id}/results`); setData(data); }
        catch { setData({ error: true }); }
    };
    useEffect(() => { load(); }, [id]);
    // Tick every second for live countdown; if polls just closed, refetch results.
    useEffect(() => {
        const t = setInterval(() => {
            setTick((x) => x + 1);
            if (data && data.is_over === false && data.election?.ends_at) {
                if (new Date(data.election.ends_at).getTime() <= Date.now()) {
                    load();
                }
            }
        }, 1000);
        return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    if (!data) return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-6 py-10 hash-mono text-slate-500 text-sm">computing tally…</div>
        </div>
    );
    if (data.error) return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-6 py-10">Election not found.</div>
        </div>
    );

    // Locked / sealed state
    if (data.results_sealed) {
        const remaining = countdown(data.election?.ends_at);
        return (
            <div className="min-h-screen bg-slate-50">
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <Link to="/results" className="text-sm text-civic-600 underline-offset-4 hover:underline" data-testid="back-results">← All results</Link>
                    <div className="mt-3">
                        <div className="overline text-civic-600 flex items-center gap-2 flex-wrap">
                            <span>{data.election.status?.toUpperCase()} · POLLS OPEN</span>
                            {data.election.secure_election && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 normal-case" style={{ letterSpacing: "0.08em" }} data-testid="detail-secure-badge">
                                    <Camera size={11} weight="fill" /> Secure · photo-verified
                                </span>
                            )}
                        </div>
                        <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tighter mt-1 text-slate-900 break-words leading-tight">{data.election.title}</h1>
                        {data.election.description && <p className="text-sm text-slate-600 mt-2">{data.election.description}</p>}
                    </div>
                    <Card className="mt-8 p-8 bg-white border-2 border-amber-200" data-testid="results-sealed">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 grid place-items-center rounded-md bg-amber-50 border border-amber-200 shrink-0">
                                <Lock size={24} weight="fill" className="text-amber-600" />
                            </div>
                            <div className="min-w-0">
                                <div className="overline text-amber-700">Results sealed</div>
                                <h2 className="font-heading font-bold text-2xl text-slate-900 mt-1">Tallies are hidden until polls close</h2>
                                <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                                    To preserve fairness and prevent vote-pull effects, no one — including the election authority and platform admins —
                                    can see counts while the election is still open. Results unlock automatically when the polls close.
                                </p>
                                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="results-countdown">
                                        <div className="overline text-slate-500">Time remaining</div>
                                        <div className="font-heading font-black text-3xl text-civic-700 mt-1 hash-mono">
                                            {remaining || "Closing…"}
                                        </div>
                                        <div className="text-[11px] text-slate-500 mt-2">
                                            Closes at <span className="hash-mono">{fmtDt(data.election.ends_at)}</span>
                                        </div>
                                    </div>
                                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                                        <div className="overline text-slate-500">Authority</div>
                                        <div className="font-semibold text-slate-900 mt-1">{data.election.authority || "—"}</div>
                                        <div className="text-[11px] text-slate-500 mt-2">
                                            Opened at <span className="hash-mono">{fmtDt(data.election.starts_at)}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-wrap gap-3">
                                    <Link to="/explorer"><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium">Open chain explorer →</button></Link>
                                    <Link to={`/audit?election=${id}`}><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium" data-testid="audit-link">Audit (totals only) →</button></Link>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Election closed but authority has toggled "Hide results" — show a
    // friendly withheld-tally screen instead of numbers. Admins never hit
    // this branch (backend bypasses the flag for authenticated admins).
    if (data.results_hidden) {
        return (
            <div className="min-h-screen bg-slate-50">
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                    <Link to="/results" className="text-sm text-civic-600 underline-offset-4 hover:underline" data-testid="back-results">← All results</Link>
                    <div className="mt-3">
                        <div className="overline text-civic-600 flex items-center gap-2 flex-wrap">
                            <span>{data.election.status?.toUpperCase()} · RESULTS HIDDEN</span>
                            {data.election.secure_election && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 normal-case" style={{ letterSpacing: "0.08em" }}>
                                    <Camera size={11} weight="fill" /> Secure · photo-verified
                                </span>
                            )}
                        </div>
                        <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tighter mt-1 text-slate-900 break-words leading-tight">{data.election.title}</h1>
                        {data.election.description && <p className="text-sm text-slate-600 mt-2">{data.election.description}</p>}
                    </div>
                    <Card className="mt-8 p-8 bg-white border-2 border-amber-200" data-testid="results-hidden">
                        <div className="flex items-start gap-4">
                            <div className="h-12 w-12 grid place-items-center rounded-md bg-amber-50 border border-amber-200 shrink-0">
                                <Lock size={24} weight="fill" className="text-amber-600" />
                            </div>
                            <div className="min-w-0">
                                <div className="overline text-amber-700">Results withheld</div>
                                <h2 className="font-heading font-bold text-2xl text-slate-900 mt-1">Tallies have been hidden by the election authority</h2>
                                <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                                    Polls have closed, but the election authority has not yet published the final tallies.
                                    Individual votes remain sealed on the blockchain and can be independently verified;
                                    only the summary counts are being withheld here. Check back once the authority releases the results.
                                </p>
                                <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-4">
                                    <div className="overline text-slate-500">Authority</div>
                                    <div className="font-semibold text-slate-900 mt-1">{data.election.authority || "—"}</div>
                                    <div className="text-[11px] text-slate-500 mt-2">
                                        Closed at <span className="hash-mono">{fmtDt(data.election.closed_at || data.election.ends_at)}</span>
                                    </div>
                                </div>
                                <div className="mt-5 flex flex-wrap gap-3">
                                    <Link to="/explorer"><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium">Open chain explorer →</button></Link>
                                    <Link to={`/audit?election=${id}`}><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium">Audit (totals only) →</button></Link>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Polls closed — full tallies visible
    const top = data.results[0];
    const total = data.total_votes || 1;
    const questions = data.questions || [];
    const hasCandidates = (data.results || []).length > 0;

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
                <Link to="/results" className="text-sm text-civic-600 underline-offset-4 hover:underline" data-testid="back-results">← All results</Link>
                <div className="mt-3 flex items-end justify-between flex-wrap gap-3">
                    <div className="min-w-0">
                        <div className="overline text-civic-600 flex items-center gap-2 flex-wrap">
                            <span>POLLS CLOSED · {data.total_votes} votes</span>
                            {data.election.secure_election && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 normal-case" style={{ letterSpacing: "0.08em" }} data-testid="detail-secure-badge">
                                    <Camera size={11} weight="fill" /> Secure · photo-verified
                                </span>
                            )}
                        </div>
                        <h1 className="font-heading font-black text-2xl sm:text-4xl tracking-tighter mt-1 text-slate-900 break-words leading-tight">{data.election.title}</h1>
                        {data.election.description && (
                            <p className="text-sm text-slate-600 mt-1 max-w-2xl break-words">{data.election.description}</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <WhoVotedButton id={id} total={data.total_votes} eligible={data.eligible_voters} />
                            {typeof data.eligible_voters === "number" && data.eligible_voters > 0 && (
                                <div className="inline-flex items-center gap-2 rounded-md border border-civic-200 bg-civic-50/70 px-2.5 h-9 hash-mono text-[11px] text-civic-900" data-testid="turnout-card">
                                    <Users size={12} weight="fill" className="text-civic-700" />
                                    <span>
                                        <span className="font-semibold text-slate-900" data-testid="turnout-total">{data.total_votes || 0}</span>
                                        <span className="text-slate-400"> / </span>
                                        <span className="font-semibold text-slate-900" data-testid="turnout-eligible">{data.eligible_voters}</span>
                                        <span className="text-slate-500"> voters</span>
                                    </span>
                                    <span className="text-slate-300">·</span>
                                    <span className="font-semibold text-civic-700" data-testid="turnout-pct">
                                        {Math.round(((data.total_votes || 0) / data.eligible_voters) * 100)}% turnout
                                    </span>
                                </div>
                            )}
                            <Button
                                variant="outline"
                                onClick={() => printCertifiedSheet(data)}
                                className="h-9 text-sm border-civic-200 hover:bg-civic-50 no-print"
                                data-testid="print-results-btn"
                                title="Print a certified tally sheet"
                            >
                                <Printer size={14} weight="bold" className="mr-1.5" /> Print
                            </Button>
                        </div>
                    </div>
                    {top && data.total_votes > 0 && hasCandidates && (
                        <Card className="px-5 py-3 bg-civic-600 text-white border-civic-600 flex items-center gap-3" data-testid="leading-candidate">
                            <Trophy size={22} weight="fill" />
                            <div>
                                <div className="overline text-civic-100">Winner</div>
                                <div className="font-heading font-bold text-lg">{top.name}</div>
                            </div>
                        </Card>
                    )}
                </div>

                {/* Election meta strip — authority, when, how long */}
                <ElectionMetaStrip election={data.election} />

                {hasCandidates && (
                    <>
                        <div className="mt-6 overline text-slate-500">Candidates</div>
                        <div className="mt-2 space-y-2" data-testid="results-bars">
                            {data.results.map((r, i) => {
                                const pct = total > 0 ? (r.votes / total) * 100 : 0;
                                return (
                                    <motion.div
                                        key={r.candidate_id}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: i * 0.05 }}
                                    >
                                        <Card className="p-3 bg-white border-slate-200" data-testid={`result-row-${r.candidate_id}`}>
                                            <div className="flex items-center justify-between gap-3 mb-1">
                                                <div className="min-w-0 flex items-baseline gap-2 truncate">
                                                    <span className="font-semibold text-sm text-slate-900 truncate">{r.name}</span>
                                                    {r.party && <span className="text-[10px] hash-mono text-civic-700 truncate">{r.party}</span>}
                                                </div>
                                                <div className="hash-mono shrink-0 text-sm">
                                                    <span className="font-semibold text-slate-900" data-testid={`result-votes-${r.candidate_id}`}>{r.votes}</span>
                                                    <span className="text-slate-500 ml-1.5">{pct.toFixed(1)}%</span>
                                                </div>
                                            </div>
                                            <div className="h-1.5 rounded-sm bg-slate-100 overflow-hidden">
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${pct}%` }}
                                                    transition={{ duration: 0.6, delay: 0.1 + i * 0.05 }}
                                                    className={`h-full ${i === 0 && r.votes > 0 ? "bg-civic-600" : "bg-civic-300"}`}
                                                />
                                            </div>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </>
                )}

                {questions.length > 0 && (
                    <>
                        <div className={`${hasCandidates ? "mt-6" : "mt-6"} overline text-slate-500`}>Ballot questions</div>
                        <div className="mt-2 space-y-2" data-testid="results-questions">
                            {questions.map((q, qi) => {
                                const tallyEntries = Object.entries(q.tally || {});
                                const isMultiSelect = q.type === "multi" && (q.max_selections || 1) > 1;
                                const isRating = q.type === "rating";
                                const isText = q.type === "text_response";
                                const qTotal = isText
                                    ? (q.response_count || 0)
                                    : (isRating
                                        ? (q.response_count || 0)
                                        : (isMultiSelect
                                            ? (q.response_count || 0)
                                            : tallyEntries.reduce((s, [, v]) => s + (v || 0), 0)));
                                const sorted = [...tallyEntries].sort((a, b) => b[1] - a[1]);
                                const winnerVal = sorted.length > 0 ? sorted[0][1] : 0;
                                const typeLabel = q.type === "yes_no" ? "Yes / No"
                                    : q.type === "for_against" ? "For / Against"
                                    : isRating ? "Rating 1–10"
                                    : isText ? "Text Response"
                                    : isMultiSelect ? `Pick up to ${q.max_selections}`
                                    : "Multi-choice";
                                return (
                                    <motion.div
                                        key={q.question_id}
                                        initial={{ opacity: 0, y: 6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3, delay: qi * 0.04 }}
                                    >
                                        <Card className="p-3 sm:p-4 bg-white border-slate-200 overflow-hidden" data-testid={`question-result-${q.question_id}`}>
                                            <div className="grid md:grid-cols-12 gap-3 md:gap-4 items-start">
                                                <div className="md:col-span-4 min-w-0">
                                                    <div className="overline text-civic-700 text-[10px] break-words">P{qi + 1} · {typeLabel}</div>
                                                    <h3 className="font-heading font-bold text-base text-slate-900 leading-snug mt-1 break-words">{q.title}</h3>
                                                    {q.text && <p className="text-xs text-slate-500 mt-1 leading-snug line-clamp-3 break-words">{q.text}</p>}
                                                    <div className="hash-mono text-[11px] text-slate-500 mt-2">
                                                        <span className="font-semibold text-slate-900">{qTotal}</span> {isText ? "responses" : "voters answered"}
                                                    </div>
                                                </div>
                                                <div className="md:col-span-8 min-w-0 w-full">
                                                    {isText ? (
                                                        <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1" data-testid={`question-responses-${q.question_id}`}>
                                                            {(q.responses || []).length === 0 ? (
                                                                <div className="text-xs text-slate-400 italic">No responses submitted.</div>
                                                            ) : (
                                                                (q.responses || []).map((r, ri) => (
                                                                    <div key={ri} className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1 leading-snug break-words" data-testid={`response-${q.question_id}-${ri}`}>
                                                                        {r}
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    ) : isRating ? (
                                                        <RatingBlock q={q} />
                                                    ) : (
                                                        <ChoiceChart q={q} entries={sorted} total={qTotal} winnerVal={winnerVal} />
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </>
                )}

                {!hasCandidates && questions.length === 0 && (
                    <Card className="mt-8 p-10 text-center bg-white border-slate-200" data-testid="no-results">
                        <ChartBar size={32} weight="duotone" className="text-civic-600 mx-auto mb-3" />
                        <div className="font-heading font-bold text-lg text-slate-900">No ballot items</div>
                        <p className="text-sm text-slate-500 mt-2">This election had neither candidates nor ballot questions.</p>
                    </Card>
                )}

                <PrecinctTurnout rows={data.precinct_turnout} />

                <div className="mt-8 flex flex-wrap gap-3 items-center">
                    <WhoVotedButton id={id} />
                    <Link to={`/audit?election=${id}`}><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium" data-testid="audit-link">Audit this election →</button></Link>
                    <Link to="/explorer"><button className="text-civic-600 underline-offset-4 hover:underline text-sm font-medium">Open chain explorer →</button></Link>
                </div>
            </div>

            {/* Signature block (print only) — small "certified by" area above the page footer. */}
            <div className="print-only-signature print-only" aria-hidden="true">
                <div className="print-only-signature-title">Certified this tally as accurate on behalf of the election authority:</div>
                <div className="print-only-signature-grid">
                    <div className="print-only-signature-field">
                        <div className="print-only-signature-line" />
                        <div className="print-only-signature-label">Signature</div>
                    </div>
                    <div className="print-only-signature-field">
                        <div className="print-only-signature-line" />
                        <div className="print-only-signature-label">Printed name</div>
                    </div>
                    <div className="print-only-signature-field">
                        <div className="print-only-signature-line" />
                        <div className="print-only-signature-label">Title / Authority ({data.election.authority || "—"})</div>
                    </div>
                    <div className="print-only-signature-field">
                        <div className="print-only-signature-line" />
                        <div className="print-only-signature-label">Date</div>
                    </div>
                </div>
            </div>

            {/* Browser-agnostic DOM footer — appears at the end of the printed
                content so Safari / Firefox users (which ignore CSS `@page` margin
                boxes) still get the certification stamp. Chromium users get this
                *plus* the per-page `@bottom-*` footer injected at print time. */}
            <div className="print-only-endstamp print-only" aria-hidden="true" data-testid="print-endstamp">
                <div className="print-only-endstamp-row">
                    <span className="print-only-endstamp-title">{data.election.title}</span>
                    <span className="print-only-endstamp-sep">·</span>
                    <span>{data.election.authority || "—"}</span>
                </div>
                <div className="print-only-endstamp-row print-only-endstamp-row-2">
                    <span>Certified tally · <strong>{data.total_votes}</strong> of {data.eligible_voters ?? "—"} voters{typeof data.eligible_voters === "number" && data.eligible_voters > 0 ? ` (${Math.round((data.total_votes / data.eligible_voters) * 100)}%)` : ""}</span>
                    <span className="print-only-endstamp-sep">·</span>
                    <span>Printed {new Date().toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                </div>
            </div>
        </div>
    );
}

function PrecinctTurnout({ rows }) {
    if (!rows || rows.length === 0) return null;
    return (
        <section className="mt-8" data-testid="precinct-turnout">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div className="overline text-slate-500 flex items-center gap-1.5"><MapPin size={12} weight="fill" /> Turnout by precinct</div>
                <div className="text-[11px] text-slate-500">Participation only — how each precinct voted is never broken out.</div>
            </div>
            <Card className="mt-2 bg-white border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {rows.map((r) => (
                    <div key={r.precinct} className="px-4 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[10rem_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5" data-testid={`precinct-row-${r.precinct}`}>
                        <div className="hash-mono text-sm font-semibold text-slate-900 truncate">{r.precinct}</div>
                        <div className="text-right sm:order-3 hash-mono text-xs text-slate-600 whitespace-nowrap" data-testid={`precinct-count-${r.precinct}`}>
                            <span className="font-semibold text-slate-900">{r.voted}</span> / {r.eligible} · <span className="font-semibold text-civic-700">{Math.round(r.turnout_pct)}%</span>
                        </div>
                        <div className="col-span-2 sm:col-span-1 sm:order-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full bg-civic-600 rounded-full transition-[width] duration-700" style={{ width: `${Math.min(100, r.turnout_pct)}%` }} />
                        </div>
                    </div>
                ))}
            </Card>
        </section>
    );
}

function WhoVotedButton({ id, total, eligible }) {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");

    const load = async () => {
        try { const { data } = await api.get(`/elections/${id}/voters`); setData(data); }
        catch { setData({ error: true }); }
    };
    const onOpen = () => { setOpen(true); if (!data) load(); };

    const filtered = (data?.voters || []).filter((v) => {
        if (!q.trim()) return true;
        const needle = q.trim().toLowerCase();
        return [v.name, v.voter_id, v.precinct, v.address, v.ip].some((f) => (f || "").toLowerCase().includes(needle));
    });

    // ─── CSV export ───────────────────────────────────────────────────────────
    const downloadCsv = () => {
        if (!data?.voters) return;
        const admin = !!data.viewer_is_admin;
        const cols = admin
            ? ["Name", "Voter ID", "Address", "Precinct", "IP"]
            : ["Name", "Address", "IP"];
        const esc = (v) => {
            const s = (v ?? "").toString();
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = data.voters.map((v) => admin
            ? [v.name, v.voter_id, v.address, v.precinct, v.ip || ""]
            : [v.name, v.address, v.ip || ""]
        );
        const csv = [cols.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
        const safeTitle = (data.election_title || "election").replace(/[^\w\-]+/g, "_");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${safeTitle}_participation_roster.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <Button variant="outline" onClick={onOpen} className="h-9 text-sm border-civic-200 hover:bg-civic-50" data-testid="who-voted-btn">
                <Users size={14} className="mr-1.5" weight="bold" /> Who voted
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-4xl max-h-[80vh] flex flex-col p-0 gap-0" data-testid="who-voted-dialog">
                    <DialogHeader className="p-5 pb-3 border-b border-slate-200">
                        <div className="flex items-center gap-2">
                            <Users size={18} weight="duotone" className="text-civic-600" />
                            <DialogTitle className="font-heading text-lg">Who voted</DialogTitle>
                        </div>
                        <DialogDescription className="text-xs">
                            {data?.error
                                ? "Could not load participation list."
                                : data
                                    ? `${data.total} voter${data.total === 1 ? "" : "s"} participated. Choices remain anonymous on the chain — only participation is shown here.`
                                    : "Loading…"}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="px-5 pt-3 flex items-center gap-2">
                        <div className="relative flex-1">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder={data?.viewer_is_admin ? "Search by name, voter ID, address, precinct, IP" : "Search by name, address, IP"}
                                className="pl-8 h-9 text-sm"
                                data-testid="who-voted-search"
                            />
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadCsv}
                            disabled={!data || !!data.error || (data?.voters?.length ?? 0) === 0}
                            className="h-9 shrink-0 border-civic-200 hover:bg-civic-50 text-civic-700"
                            data-testid="who-voted-csv"
                            title="Download this participation roster as CSV"
                        >
                            <Download size={13} weight="bold" className="mr-1.5" /> CSV
                        </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-5 py-3">
                        {!data ? (
                            <div className="hash-mono text-slate-500 text-xs">loading…</div>
                        ) : data.error ? (
                            <div className="text-sm text-red-700">Could not load.</div>
                        ) : filtered.length === 0 ? (
                            <div className="text-sm text-slate-500 italic py-4 text-center">{data.total === 0 ? "No votes yet for this election." : "No voters match your search."}</div>
                        ) : (
                            <div className="rounded-md border border-slate-200 overflow-x-auto" data-testid="who-voted-list">
                                {(() => {
                                    const showVoterId = !!data.viewer_is_admin;
                                    const showPhoto = !!data.secure_election;
                                    return (
                                        <table className="w-full text-sm">
                                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                                <tr>
                                                    {showPhoto && <th className="text-left font-medium px-3 py-2">Photo</th>}
                                                    <th className="text-left font-medium px-3 py-2">Name</th>
                                                    {showVoterId && <th className="text-left font-medium px-3 py-2">Voter ID</th>}
                                                    <th className="text-left font-medium px-3 py-2">Address</th>
                                                    <th className="text-left font-medium px-3 py-2 whitespace-nowrap">IP</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {filtered.map((v, i) => (
                                                    <tr
                                                        key={(v.voter_id || v.name) + i}
                                                        className="hover:bg-slate-50"
                                                        data-testid={`who-voted-row-${v.voter_id || i}`}
                                                    >
                                                        {showPhoto && (
                                                            <td className="px-3 py-2 align-middle">
                                                                {v.face_photo_url ? (
                                                                    <a href={`${BACKEND_URL}${v.face_photo_url}`} target="_blank" rel="noreferrer">
                                                                        <img
                                                                            src={`${BACKEND_URL}${v.face_photo_url}`}
                                                                            alt={v.name}
                                                                            className="h-9 w-9 rounded object-cover border border-slate-200 hover:ring-2 hover:ring-civic-400 transition-all"
                                                                            data-testid={`who-voted-photo-${v.voter_id || i}`}
                                                                        />
                                                                    </a>
                                                                ) : (
                                                                    <div className="h-9 w-9 rounded bg-slate-100 grid place-items-center text-[9px] text-slate-400 italic">no photo</div>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className="px-3 py-2 align-middle font-semibold text-slate-900 whitespace-nowrap">{v.name}</td>
                                                        {showVoterId && (
                                                            <td className="px-3 py-2 align-middle hash-mono text-civic-700 text-xs whitespace-nowrap">{v.voter_id}</td>
                                                        )}
                                                        <td className="px-3 py-2 align-middle text-xs text-slate-700 max-w-[220px] truncate" title={v.address || ""}>
                                                            {v.address || <span className="italic text-slate-400">—</span>}
                                                        </td>
                                                        <td
                                                            className="px-3 py-2 align-middle text-xs hash-mono whitespace-nowrap"
                                                            data-testid={`who-voted-ip-${v.voter_id || i}`}
                                                        >
                                                            {v.ip ? (
                                                                <span className="inline-flex items-center gap-1">
                                                                    <span className="text-slate-700">{v.ip}</span>
                                                                    {v.is_vpn && (
                                                                        <span
                                                                            className="text-[9px] px-1 rounded bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wider"
                                                                            title="VPN detected"
                                                                        >
                                                                            VPN
                                                                        </span>
                                                                    )}
                                                                    {v.is_hosting && (
                                                                        <span
                                                                            className="text-[9px] px-1 rounded bg-rose-50 text-rose-700 border border-rose-200 uppercase tracking-wider"
                                                                            title="Datacenter / hosting IP"
                                                                        >
                                                                            DC
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            ) : (
                                                                <span className="italic text-slate-400">—</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

function ElectionMetaStrip({ election }) {
    if (!election) return null;
    const startsAt = election.starts_at ? new Date(election.starts_at) : null;
    const endsAt = election.ends_at ? new Date(election.ends_at) : null;
    const closedAt = election.closed_at ? new Date(election.closed_at) : null;
    const fmt = (d) => d ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

    const durationStr = (ms) => {
        if (ms == null || !isFinite(ms) || ms < 0) return "—";
        const d = Math.floor(ms / 86400000);
        const h = Math.floor((ms % 86400000) / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        if (d > 0) return `${d}d ${h}h${m ? ` ${m}m` : ""}`;
        if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
        return `${m}m`;
    };

    const scheduledMs = startsAt && endsAt ? Math.max(0, endsAt.getTime() - startsAt.getTime()) : null;
    const scheduledDuration = scheduledMs != null ? durationStr(scheduledMs) : "—";

    // "Closed early" is only when we have an explicit closed_at AND it beats
    // the scheduled ends_at by at least a minute (guards against millisecond
    // race conditions between the schedule and the auto-close trigger).
    const wasClosedEarly =
        !!closedAt && !!endsAt && (endsAt.getTime() - closedAt.getTime() >= 60_000);

    const actualMs = wasClosedEarly && startsAt ? Math.max(0, closedAt.getTime() - startsAt.getTime()) : null;
    const actualDuration = actualMs != null ? durationStr(actualMs) : null;

    const reason = election.closed_reason;
    const reasonLabel = reason === "auto_close_full_turnout"
        ? "100% turnout — auto-closed"
        : reason === "admin_manual_close"
            ? "Closed by admin"
            : null;
    const reasonIcon = reason === "auto_close_full_turnout" ? Flag : UserCircleGear;
    const ReasonIcon = reasonIcon;
    const reasonTone = reason === "auto_close_full_turnout"
        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
        : "bg-amber-50 border-amber-200 text-amber-800";

    return (
        <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="election-meta-strip">
            <Card className="p-3 bg-white border-slate-200">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                    <ShieldCheck size={11} weight="bold" /> Election authority
                </div>
                <div className="font-heading font-semibold text-sm text-slate-900 mt-1 truncate" data-testid="meta-authority">
                    {election.authority || <span className="italic text-slate-400 font-normal">Not specified</span>}
                </div>
            </Card>

            <Card className="p-3 bg-white border-slate-200">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                    <Calendar size={11} weight="bold" /> Polling window
                </div>
                <div className="hash-mono text-xs text-slate-900 mt-1 leading-snug" data-testid="meta-window">
                    <div>{fmt(startsAt)}</div>
                    <div className="text-slate-500">→ {fmt(endsAt)}
                        {!wasClosedEarly && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider text-slate-400 font-sans not-italic">scheduled end</span>
                        )}
                    </div>
                    {wasClosedEarly && (
                        <div className="mt-1.5 pt-1.5 border-t border-slate-100" data-testid="meta-window-early">
                            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-rose-700 font-sans font-bold">
                                <Flag size={10} weight="fill" /> Closed early
                            </div>
                            <div className="text-rose-700 font-semibold">{fmt(closedAt)}</div>
                        </div>
                    )}
                </div>
            </Card>

            <Card className="p-3 bg-white border-slate-200">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                    <Timer size={11} weight="bold" /> Duration
                </div>
                <div className="mt-1" data-testid="meta-duration">
                    <div className={`font-heading font-bold text-base hash-mono ${wasClosedEarly ? "text-slate-500 line-through decoration-slate-400/60" : "text-slate-900"}`}>
                        {scheduledDuration}
                        {wasClosedEarly && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-wider text-slate-400 font-sans font-semibold no-underline">scheduled</span>
                        )}
                    </div>
                    {wasClosedEarly && (
                        <>
                            <div className="mt-1 font-heading font-black text-lg text-rose-700 hash-mono" data-testid="meta-duration-actual">
                                {actualDuration}
                                <span className="ml-1.5 text-[9px] uppercase tracking-wider text-rose-500 font-sans font-semibold">actual</span>
                            </div>
                            {reasonLabel && (
                                <div
                                    className={`mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold border ${reasonTone}`}
                                    data-testid="meta-close-reason"
                                    title={election.closed_by_email ? `by ${election.closed_by_email}` : undefined}
                                >
                                    <ReasonIcon size={10} weight="fill" /> {reasonLabel}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
}

// ————————— Chart palette for ballot-question results —————————
const CHART_PALETTE = [
    "#0d6efd", // civic blue (winner-ish)
    "#10b981", // emerald
    "#f59e0b", // amber
    "#e11d48", // rose
    "#0ea5e9", // sky
    "#8b5cf6", // violet
    "#f97316", // orange
    "#14b8a6", // teal
    "#dc2626", // red
    "#65a30d", // lime
];

function ChoiceChart({ q, entries, total, winnerVal }) {
    // For multi-select questions (pick-up-to-N), the sum of individual option
    // counts exceeds the voter total. The donut slices must be sized against
    // the SUM of all selections, not the voter count — otherwise slices
    // overflow the ring and some options disappear from the chart entirely.
    const sliceTotal = entries.reduce((s, [, c]) => s + c, 0);

    // Empty state
    if (sliceTotal === 0 || entries.length === 0) {
        return (
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
                <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-full border-8 border-slate-100 grid place-items-center text-slate-300">
                    <span className="text-[10px] uppercase tracking-widest">no votes</span>
                </div>
                <div className="text-xs text-slate-400 italic">No votes recorded for this question.</div>
            </div>
        );
    }

    // Donut geometry
    const RADIUS = 40;
    const CIRC = 2 * Math.PI * RADIUS;
    let offset = 0;

    return (
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5" data-testid={`donut-wrap-${q.question_id}`}>
            <svg viewBox="0 0 100 100" className="w-28 h-28 sm:w-32 sm:h-32 shrink-0 -rotate-90" role="img" aria-label={`${q.title} results donut`}>
                <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="#f1f5f9" strokeWidth="14" />
                {entries.map(([opt, count], i) => {
                    if (count === 0) return null;
                    const seg = (count / sliceTotal) * CIRC;
                    const dashArr = `${seg} ${CIRC - seg}`;
                    const dashOff = -offset;
                    offset += seg;
                    return (
                        <circle
                            key={opt}
                            cx="50" cy="50" r={RADIUS}
                            fill="none"
                            stroke={CHART_PALETTE[i % CHART_PALETTE.length]}
                            strokeWidth="14"
                            strokeDasharray={dashArr}
                            strokeDashoffset={dashOff}
                            strokeLinecap="butt"
                            data-testid={`donut-seg-${q.question_id}-${opt}`}
                        />
                    );
                })}
            </svg>

            <div className="w-full sm:flex-1 min-w-0 space-y-1.5" data-testid={`legend-${q.question_id}`}>
                {entries.map(([opt, count], i) => {
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    const isWinner = count > 0 && count === winnerVal;
                    const color = CHART_PALETTE[i % CHART_PALETTE.length];
                    return (
                        <div key={opt} className="flex items-start gap-2 text-xs min-w-0" data-testid={`question-option-${q.question_id}-${opt}`}>
                            <span className="h-3 w-3 rounded-sm shrink-0 mt-1" style={{ backgroundColor: count > 0 ? color : "#e2e8f0" }} aria-hidden="true" />
                            <span className={`flex-1 min-w-0 break-words ${isWinner ? "text-civic-700 font-semibold" : "text-slate-800"}`}>{opt}</span>
                            <span className="hash-mono shrink-0 text-[11px] tabular-nums whitespace-nowrap self-center">
                                <span className={`font-semibold ${isWinner ? "text-civic-700" : "text-slate-900"}`}>{count}</span>
                                <span className="text-slate-500 ml-1.5">{pct.toFixed(1)}%</span>
                            </span>
                        </div>
                    );
                })}
                {sliceTotal > total && (
                    <div
                        className="pt-1 mt-1 border-t border-slate-100 hash-mono text-[10px] text-slate-500 italic"
                        data-testid={`legend-selections-${q.question_id}`}
                        title="Voters could pick multiple options, so the pie shows each option's share of the total selections cast."
                    >
                        <span className="font-semibold text-slate-700">{sliceTotal}</span> selection{sliceTotal === 1 ? "" : "s"} cast by {total} voter{total === 1 ? "" : "s"}
                    </div>
                )}
            </div>
        </div>
    );
}

function RatingBlock({ q }) {
    const tally = q.tally || {};
    const count = q.response_count || 0;
    const avg = q.average;
    // Winner bucket = the rating value(s) with the max count
    const maxCnt = Math.max(0, ...Object.values(tally));

    return (
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-5" data-testid={`rating-block-${q.question_id}`}>
            <div className="shrink-0 h-28 w-28 sm:h-32 sm:w-32 rounded-full border-8 border-civic-100 grid place-items-center bg-white">
                <div className="text-center">
                    <div className="font-heading font-black text-4xl text-civic-700 leading-none tabular-nums" data-testid={`question-avg-${q.question_id}`}>
                        {avg != null ? avg : "—"}
                    </div>
                    <div className="overline text-slate-500 text-[9px] mt-1">Average / 10</div>
                </div>
            </div>
            <div className="w-full sm:flex-1 min-w-0" data-testid={`rating-histogram-${q.question_id}`}>
                <div className="flex items-end gap-0.5 sm:gap-1 h-20 sm:h-24">
                    {["1","2","3","4","5","6","7","8","9","10"].map((n) => {
                        const c = tally[n] || 0;
                        const h = maxCnt > 0 ? Math.max(4, (c / maxCnt) * 100) : 4;
                        const isPeak = c > 0 && c === maxCnt;
                        return (
                            <div key={n} className="flex-1 flex flex-col items-center justify-end h-full min-w-0" data-testid={`rating-bucket-${q.question_id}-${n}`}>
                                <div className="text-[9px] sm:text-[10px] hash-mono text-slate-500 mb-0.5 tabular-nums">{c > 0 ? c : ""}</div>
                                <motion.div
                                    initial={{ height: 0 }}
                                    animate={{ height: `${h}%` }}
                                    transition={{ duration: 0.5, delay: 0.05 * parseInt(n, 10) }}
                                    className={`w-full rounded-t-sm ${isPeak ? "bg-civic-600" : c > 0 ? "bg-civic-300" : "bg-slate-100"}`}
                                    aria-hidden="true"
                                />
                                <div className={`text-[9px] sm:text-[10px] hash-mono mt-1 tabular-nums ${isPeak ? "text-civic-700 font-semibold" : "text-slate-500"}`}>{n}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-1 hash-mono text-[9px] sm:text-[10px] text-slate-500 flex justify-between gap-2">
                    <span className="whitespace-nowrap"><span className="sm:hidden">Disagree</span><span className="hidden sm:inline">Strongly disagree</span></span>
                    <span className="whitespace-nowrap text-center">{count} rated</span>
                    <span className="whitespace-nowrap"><span className="sm:hidden">Agree</span><span className="hidden sm:inline">Strongly agree</span></span>
                </div>
            </div>
        </div>
    );
}

